import { ConditionBase, ConditionBuilder } from '../condition.type';

export interface ConditionEquals extends ConditionBase {
  readonly equals: unknown;
  /**
   * Whether the type-safe check (===) should be used
   *
   * @default true
   */
  readonly typeSafe?: boolean;
}

/**
 * Type guard that checks if an object fulfills the `ConditionEquals` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionEquals`
 */
export const isConditionEquals = (
  c: Record<string, any>,
): c is ConditionEquals => 'equals' in c;

/**
 * @param condition Condition object
 *
 * @returns
 * Condition function that returns `true` if the value is
 * equal to the given value, otherwise `false`.
 *
 * @example
 * equals({equals:'value'})('value') // true
 * equals({equals:'value'})('other') // false
 *
 * @example
 * equals({equals:'0', typeSafe: false})(0) // true
 * equals({equals:1, typeSafe: false})('1') // true
 */
export const equals: ConditionBuilder<ConditionEquals, unknown> = (
  condition,
) => {
  const { equals: otherValue, typeSafe = true } = condition;

  return typeSafe
    ? (value) => otherValue === value
    : // eslint-disable-next-line eqeqeq
      (value) => otherValue == value;
};
